import React, { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  Plus,
  BarChart3,
  Eye,
  ChevronDown,
  Settings2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/layout/AdminLayout";
import MediaPicker from "@/components/ui/media-picker";

import {
  sendImmediateNotification,
  scheduleNotificationCampaign,
  getTopicSubscriberCounts,
  type SendNotificationRequest,
} from "@/api/notifications";
import type { TopicSubscriberCounts } from "@/types/notifications";
import { refreshGroupMemberCount, getGroupMembers } from "@/api/user-groups";
import TemplateSelector from "@/components/notifications/TemplateSelector";
import TargetAudienceSelector from "@/components/notifications/TargetAudienceSelector";
import NotificationSummary from "@/components/notifications/NotificationSummary";

interface NotificationTemplate {
  id: number;
  name: string;
  category: string;
  type: string;
  title_template: string;
  body_template: string;
  action_url: string | null;
  image_url: string | null;
  variables: Record<string, unknown>;
  description: string;
  is_active: boolean;
  created_at: string;
}

interface BookOption {
  id: number;
  title: string;
  authors: string;
  coverImage?: string;
}

// Variables auto-filled by book selection
const BOOK_VARIABLES = ["book_id", "book_title", "book_author"];

// Variables that are always auto-populated
const AUTO_POPULATED_VARIABLES = ["name"];

interface UserGroup {
  id: number;
  name: string;
  description: string;
  member_count: number;
  created_at: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  avatarUrl: string;
  isActive: boolean;
  registeredAt: string;
}

export default function SendNotification() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<{
    current: number;
    total: number;
    message: string;
  } | null>(null);

  // Data
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [topicSubscriberCounts, setTopicSubscriberCounts] =
    useState<TopicSubscriberCounts>({});

  // Form state
  const [selectedTemplate, setSelectedTemplate] =
    useState<NotificationTemplate | null>(null);
  const [selectedUserGroup, setSelectedUserGroup] = useState<UserGroup | null>(
    null
  );
  const [sendMode, setSendMode] = useState<"immediate" | "scheduled" | "draft">(
    "immediate"
  );
  const [scheduledFor, setScheduledFor] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [templateVariables, setTemplateVariables] = useState<
    Record<string, unknown>
  >({});

  // Book picker state
  const [selectedBook, setSelectedBook] = useState<BookOption | null>(null);

  // Direct title/body — always editable
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationBody, setNotificationBody] = useState("");

  // Advanced features
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [enableABTesting, setEnableABTesting] = useState(false);
  const [abTestVariants, setAbTestVariants] = useState<
    Array<{ id: string; title: string; body: string; weight: number }>
  >([]);
  const [enableAnalytics, setEnableAnalytics] = useState(true);
  const [enableDeepLinking, setEnableDeepLinking] = useState(false);
  const [deepLinkUrl, setDeepLinkUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  // Audience count
  const [groupAudienceCount, setGroupAudienceCount] = useState<{
    totalMembers: number;
    withTokens: number;
    loading: boolean;
  }>({ totalMembers: 0, withTokens: 0, loading: false });
  const [individualTokenCount, setIndividualTokenCount] = useState<{
    withTokens: number;
    loading: boolean;
  }>({ withTokens: 0, loading: false });

  // Search
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState<User[]>([]);

  // Derived preview values
  const previewTitle = useCallback(() => {
    if (!selectedTemplate) return notificationTitle;
    let title = notificationTitle || selectedTemplate.title_template;
    const sampleVars = {
      ...templateVariables,
      name: templateVariables.name || "John Doe",
      book_title: templateVariables.book_title || "The 7 Habits of Highly Effective People",
      book_author: templateVariables.book_author || "Stephen Covey",
    };
    Object.entries(sampleVars).forEach(([key, value]) => {
      if (value) {
        title = title.replace(new RegExp(`{{${key}}}`, "g"), String(value));
      }
    });
    return title;
  }, [selectedTemplate, notificationTitle, templateVariables]);

  const previewBody = useCallback(() => {
    if (!selectedTemplate) return notificationBody;
    let body = notificationBody || selectedTemplate.body_template;
    const sampleVars = {
      ...templateVariables,
      name: templateVariables.name || "John Doe",
      book_title: templateVariables.book_title || "The 7 Habits of Highly Effective People",
      book_author: templateVariables.book_author || "Stephen Covey",
    };
    Object.entries(sampleVars).forEach(([key, value]) => {
      if (value) {
        body = body.replace(new RegExp(`{{${key}}}`, "g"), String(value));
      }
    });
    return body;
  }, [selectedTemplate, notificationBody, templateVariables]);

  const hasAdvancedOptions = enableAnalytics === false || enableDeepLinking || enableABTesting || !!imageUrl;

  // Data fetching
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("notification_templates")
        .select("*")
        .eq("is_active", true)
        .eq("type", "push")
        .order("name");

      if (error) throw error;
      setTemplates((data || []) as NotificationTemplate[]);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast({
        title: "Error",
        description: "Failed to load notification templates.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchUserGroups = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("user_groups")
        .select("*")
        .order("name");

      if (error) throw error;
      setUserGroups(data || []);
    } catch (error) {
      console.error("Error fetching user groups:", error);
      toast({
        title: "Error",
        description: "Failed to load user groups.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const fetchTopicCounts = useCallback(async () => {
    try {
      const counts = await getTopicSubscriberCounts();
      setTopicSubscriberCounts(counts);
    } catch (error) {
      console.error("Error fetching topic subscriber counts:", error);
    }
  }, []);

  const searchUsersCallback = useCallback(
    async (query: string) => {
      try {
        setSearchingUsers(true);
        const { data, error } = await supabase
          .from("profiles")
          .select("id, name, email, avatar_url, is_active, registered_at")
          .or(`name.ilike.%${query}%,email.ilike.%${query}%`)
          .order("name")
          .limit(50);

        if (error) throw error;

        setUserSearchResults(
          (data || []).map((user) => ({
            id: user.id,
            name: user.name,
            email: user.email,
            avatarUrl: user.avatar_url || "",
            isActive: user.is_active,
            registeredAt: user.registered_at,
          }))
        );
      } catch (error) {
        console.error("Error searching users:", error);
        toast({
          title: "Error",
          description: `Failed to search users: ${(error as Error).message}`,
          variant: "destructive",
        });
        setUserSearchResults([]);
      } finally {
        setSearchingUsers(false);
      }
    },
    [toast]
  );

  const fetchGroupAudienceCount = useCallback(async (groupId: number) => {
    try {
      setGroupAudienceCount(prev => ({ ...prev, loading: true }));
      const totalMembers = await refreshGroupMemberCount(groupId);

      if (totalMembers === 0) {
        setGroupAudienceCount({ totalMembers: 0, withTokens: 0, loading: false });
        return;
      }

      const members = await getGroupMembers(groupId);
      const memberIds = members.map(m => m.user_id);

      const { data: tokensData, error: tokensError } = await supabase
        .from('user_push_tokens')
        .select('user_id')
        .in('user_id', memberIds)
        .eq('is_active', true);

      if (tokensError) throw tokensError;

      const usersWithTokens = new Set((tokensData || []).map(t => t.user_id));
      setGroupAudienceCount({
        totalMembers,
        withTokens: usersWithTokens.size,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching group audience count:', error);
      setGroupAudienceCount(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const fetchIndividualTokenCount = useCallback(async (userIds: number[]) => {
    if (userIds.length === 0) {
      setIndividualTokenCount({ withTokens: 0, loading: false });
      return;
    }
    try {
      setIndividualTokenCount(prev => ({ ...prev, loading: true }));
      const { data, error } = await supabase
        .from('user_push_tokens')
        .select('user_id')
        .in('user_id', userIds)
        .eq('is_active', true);

      if (error) throw error;
      const uniqueUsers = new Set((data || []).map(t => t.user_id));
      setIndividualTokenCount({ withTokens: uniqueUsers.size, loading: false });
    } catch (error) {
      console.error('Error checking individual user tokens:', error);
      setIndividualTokenCount({ withTokens: 0, loading: false });
    }
  }, []);

  // Effects
  useEffect(() => {
    fetchTemplates();
    fetchUserGroups();
    fetchTopicCounts();
  }, [fetchTemplates, fetchUserGroups, fetchTopicCounts]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (userSearchQuery.trim().length >= 2) {
        searchUsersCallback(userSearchQuery.trim());
      } else {
        setUserSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [userSearchQuery, searchUsersCallback]);

  // Handlers
  const handleTemplateChange = (templateId: string | null) => {
    if (!templateId) {
      setSelectedTemplate(null);
      setTemplateVariables({});
      setSelectedBook(null);
      return;
    }
    const template = templates.find((t) => t.id === parseInt(templateId));
    setSelectedTemplate(template || null);
    if (template) {
      // Pre-fill title and body from template
      setNotificationTitle(template.title_template);
      setNotificationBody(template.body_template);
      setSelectedBook(null);
      if (template.variables) {
        const initialVariables: Record<string, unknown> = {};
        Object.keys(template.variables).forEach((key) => {
          initialVariables[key] = "";
        });
        setTemplateVariables(initialVariables);
      } else {
        setTemplateVariables({});
      }
    }
  };

  const handleBookSelect = (book: BookOption | null) => {
    setSelectedBook(book);
    if (book) {
      // Auto-fill all book-related template variables
      setTemplateVariables((prev) => ({
        ...prev,
        book_id: String(book.id),
        book_title: book.title,
        book_author: book.authors,
      }));
    } else {
      // Clear book-related variables
      setTemplateVariables((prev) => {
        const updated = { ...prev };
        BOOK_VARIABLES.forEach((key) => {
          if (key in updated) updated[key] = "";
        });
        return updated;
      });
    }
  };

  const handleAudienceTabChange = (tab: string) => {
    if (tab === "group") {
      setSelectedUsers([]);
      setSelectedTopic(null);
      setIndividualTokenCount({ withTokens: 0, loading: false });
    } else if (tab === "individual") {
      setSelectedUserGroup(null);
      setSelectedTopic(null);
      setGroupAudienceCount({ totalMembers: 0, withTokens: 0, loading: false });
    } else if (tab === "topics") {
      setSelectedUserGroup(null);
      setSelectedUsers([]);
      setGroupAudienceCount({ totalMembers: 0, withTokens: 0, loading: false });
      setIndividualTokenCount({ withTokens: 0, loading: false });
    }
  };

  const handleUserGroupChange = (groupId: string) => {
    const group = userGroups.find((g) => g.id === parseInt(groupId));
    setSelectedUserGroup(group || null);
    setSelectedUsers([]);
    if (group) {
      fetchGroupAudienceCount(group.id);
    } else {
      setGroupAudienceCount({ totalMembers: 0, withTokens: 0, loading: false });
    }
  };

  const toggleUserSelection = (user: User) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => u.id === user.id);
      const updated = isSelected ? prev.filter((u) => u.id !== user.id) : [...prev, user];
      fetchIndividualTokenCount(updated.map(u => u.id));
      return updated;
    });
  };

  const getTargetUserCount = () => {
    if (selectedTopic) return topicSubscriberCounts[selectedTopic] ?? 0;
    if (selectedUserGroup) {
      return groupAudienceCount.totalMembers > 0
        ? groupAudienceCount.totalMembers
        : selectedUserGroup.member_count;
    }
    if (selectedUsers.length > 0) return selectedUsers.length;
    return 0;
  };

  // Check if all required template variables are filled
  const areRequiredVariablesFilled = (() => {
    if (!selectedTemplate?.variables) return true;
    const varKeys = Object.keys(selectedTemplate.variables);
    const templateText = [
      selectedTemplate.title_template || "",
      selectedTemplate.body_template || "",
      selectedTemplate.action_url || "",
    ].join(" ");

    for (const key of varKeys) {
      if (AUTO_POPULATED_VARIABLES.includes(key)) continue;
      const isUsed = templateText.includes(`{{${key}}}`);
      if (!isUsed) continue;
      const value = String(templateVariables[key] || "").trim();
      if (!value) return false;
    }
    return true;
  })();

  const canSend = !!(notificationTitle.trim() && notificationBody.trim() && getTargetUserCount() > 0 && areRequiredVariablesFilled);

  const handleSend = async () => {
    if (!notificationTitle.trim() || !notificationBody.trim()) {
      toast({ title: "Error", description: "Please enter a notification title and body.", variant: "destructive" });
      return;
    }
    if (getTargetUserCount() === 0) {
      toast({ title: "Error", description: "Please select target users or user group.", variant: "destructive" });
      return;
    }
    if (sendMode === "scheduled" && !selectedUserGroup) {
      toast({ title: "Error", description: "Scheduled campaigns only support user groups.", variant: "destructive" });
      return;
    }
    if (sendMode === "scheduled" && !scheduledFor) {
      toast({ title: "Error", description: "Please set a scheduled time.", variant: "destructive" });
      return;
    }
    if (sendMode === "draft" && !campaignName) {
      toast({ title: "Error", description: "Please enter a campaign name.", variant: "destructive" });
      return;
    }

    try {
      setSending(true);
      const request: SendNotificationRequest = {
        template_id: selectedTemplate?.id,
        title: notificationTitle,
        body: notificationBody,
        variables: templateVariables,
        send_immediately: sendMode === "immediate",
        scheduled_for: sendMode === "scheduled" ? scheduledFor : undefined,
        image_url: imageUrl || undefined,
      };

      if (selectedUserGroup) {
        request.user_group_id = selectedUserGroup.id;
      } else if (selectedUsers.length > 0) {
        request.user_ids = selectedUsers.map((u) => u.id);
      } else if (selectedTopic) {
        request.topic = selectedTopic;
      }

      let result;
      if (sendMode === "immediate") {
        result = await sendImmediateNotification(request, (progress) => {
          setSendProgress(progress);
        });
      } else {
        result = await scheduleNotificationCampaign(request);
      }

      if (result.success) {
        toast({ title: "Success", description: result.message });
        if (sendMode === "immediate") {
          resetForm();
        } else {
          navigate("/admin/notifications/campaigns");
        }
      } else {
        toast({ title: "Error", description: result.message, variant: "destructive" });
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      toast({
        title: "Error",
        description: (error as Error).message || "Failed to send notification.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
      setSendProgress(null);
    }
  };

  const resetForm = async () => {
    setSelectedTemplate(null);
    setSelectedUserGroup(null);
    setSelectedUsers([]);
    setSendMode("immediate");
    setScheduledFor("");
    setCampaignName("");
    setTemplateVariables({});
    setSelectedBook(null);
    setNotificationTitle("");
    setNotificationBody("");
    setEnableABTesting(false);
    setAbTestVariants([]);
    setEnableAnalytics(true);
    setEnableDeepLinking(false);
    setDeepLinkUrl("");
    setImageUrl("");
    setUserSearchQuery("");
    setUserSearchResults([]);
    setSendProgress(null);
    setGroupAudienceCount({ totalMembers: 0, withTokens: 0, loading: false });
    setIndividualTokenCount({ withTokens: 0, loading: false });
    setShowAdvanced(false);

    await fetchTemplates();
    await fetchUserGroups();
    await fetchTopicCounts();

    toast({ title: "Form Reset", description: "Form has been reset and data refreshed." });
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    return now.toISOString().slice(0, 16);
  };

  return (
    <AdminLayout title="Send Notifications">
      <div className="space-y-4">
        {/* ROW 1 — Slim top bar */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-muted/50 rounded-lg border">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold whitespace-nowrap">Send Notification</h1>

          {/* Conditional inputs */}
          {sendMode === "draft" && (
            <Input
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="Campaign name"
              className="h-8 max-w-[200px] text-sm"
            />
          )}
          {sendMode === "scheduled" && (
            <Input
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              min={getMinDateTime()}
              className="h-8 max-w-[220px] text-sm"
            />
          )}

          <div className="flex-1" />

          {/* Nav links */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => navigate("/admin/notifications/templates")}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Templates
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => navigate("/admin/notifications/campaigns")}
          >
            <BarChart3 className="h-3.5 w-3.5 mr-1" />
            Campaigns
          </Button>
        </div>

        {/* ROW 2 — Content + Audience (side by side) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TemplateSelector
            templates={templates}
            selectedTemplate={selectedTemplate}
            title={notificationTitle}
            body={notificationBody}
            templateVariables={templateVariables}
            selectedBook={selectedBook}
            onTemplateChange={handleTemplateChange}
            onTitleChange={setNotificationTitle}
            onBodyChange={setNotificationBody}
            onVariableChange={(key, value) =>
              setTemplateVariables((prev) => ({ ...prev, [key]: value }))
            }
            onBookSelect={handleBookSelect}
          />

          <TargetAudienceSelector
            userGroups={userGroups}
            selectedUserGroup={selectedUserGroup}
            selectedUsers={selectedUsers}
            selectedTopic={selectedTopic}
            userSearchQuery={userSearchQuery}
            searchingUsers={searchingUsers}
            userSearchResults={userSearchResults}
            groupAudienceCount={groupAudienceCount}
            topicSubscriberCounts={topicSubscriberCounts}
            sendMode={sendMode}
            onUserGroupChange={handleUserGroupChange}
            onUserSearchChange={setUserSearchQuery}
            onToggleUser={toggleUserSelection}
            onTopicChange={setSelectedTopic}
            onTabChange={handleAudienceTabChange}
          />
        </div>

        {/* ROW 3 — Preview + Summary (side by side) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Preview card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Eye className="h-4 w-4" />
                Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 border rounded-md space-y-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 dark:border-gray-700">
                <div>
                  <strong className="text-xs text-muted-foreground">Title:</strong>
                  <div className="text-sm font-medium">
                    {previewTitle() || "No title"}
                  </div>
                </div>
                <div>
                  <strong className="text-xs text-muted-foreground">Body:</strong>
                  <div className="text-sm">
                    {previewBody() || "No body"}
                  </div>
                </div>
                {(imageUrl || selectedTemplate?.image_url) && (
                  <div>
                    <strong className="text-xs text-muted-foreground">Image:</strong>
                    <div className="text-xs text-blue-600 dark:text-blue-400 break-all">
                      {imageUrl || selectedTemplate?.image_url}
                    </div>
                  </div>
                )}
                {enableDeepLinking && deepLinkUrl && (
                  <div>
                    <strong className="text-xs text-muted-foreground">Deep Link:</strong>
                    <div className="text-xs text-blue-600 dark:text-blue-400 break-all">
                      {deepLinkUrl}
                    </div>
                  </div>
                )}
                {Object.keys(templateVariables).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <strong className="text-xs text-muted-foreground">Variables:</strong>
                    <div className="text-xs text-muted-foreground/70 mt-0.5">
                      {Object.entries(templateVariables).map(([key, value]) => (
                        <div key={key}>
                          {key}: {String(value) || "(empty)"}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Summary + Send */}
          <NotificationSummary
            templateName={selectedTemplate?.name || null}
            notificationTitle={notificationTitle}
            targetUserCount={getTargetUserCount()}
            reachableUserCount={
              selectedUserGroup && groupAudienceCount.totalMembers > 0
                ? groupAudienceCount.withTokens
                : selectedUsers.length > 0
                  ? individualTokenCount.withTokens
                  : undefined
            }
            audienceLoading={groupAudienceCount.loading || individualTokenCount.loading}
            sendMode={sendMode}
            scheduledFor={scheduledFor}
            enableABTesting={enableABTesting}
            abTestVariantCount={abTestVariants.length}
            enableAnalytics={enableAnalytics}
            sending={sending}
            sendProgress={sendProgress}
            canSend={canSend}
            campaignName={campaignName}
            onSendModeChange={setSendMode}
            onSend={handleSend}
            onReset={resetForm}
          />
        </div>

        {/* ROW 4 — Collapsible Advanced Options */}
        <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
          <CollapsibleTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-between h-10"
            >
              <div className="flex items-center gap-2">
                <Settings2 className="h-4 w-4" />
                <span className="text-sm">Advanced Options</span>
                {!showAdvanced && hasAdvancedOptions && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    Active
                  </Badge>
                )}
              </div>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${showAdvanced ? "rotate-180" : ""}`}
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Card className="mt-2">
              <CardContent className="pt-6 space-y-5">
                {/* Analytics toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="adv-analytics" className="text-sm font-medium">Enable Analytics</Label>
                    <p className="text-xs text-muted-foreground">Track delivery, open, and click rates</p>
                  </div>
                  <Switch id="adv-analytics" checked={enableAnalytics} onCheckedChange={setEnableAnalytics} />
                </div>

                {/* Deep Linking */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="adv-deeplink" className="text-sm font-medium">Deep Linking</Label>
                      <p className="text-xs text-muted-foreground">Open a specific screen when tapped</p>
                    </div>
                    <Switch id="adv-deeplink" checked={enableDeepLinking} onCheckedChange={setEnableDeepLinking} />
                  </div>
                  {enableDeepLinking && (
                    <Input
                      value={deepLinkUrl}
                      onChange={(e) => setDeepLinkUrl(e.target.value)}
                      placeholder="https://yourapp.com/path"
                      className="h-9"
                    />
                  )}
                </div>

                {/* A/B Testing */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="adv-ab" className="text-sm font-medium">A/B Testing</Label>
                      <p className="text-xs text-muted-foreground">Test different content variants</p>
                    </div>
                    <Switch id="adv-ab" checked={enableABTesting} onCheckedChange={setEnableABTesting} />
                  </div>
                  {enableABTesting && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Variants</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => {
                            setAbTestVariants([
                              ...abTestVariants,
                              { id: `variant-${Date.now()}`, title: notificationTitle, body: notificationBody, weight: 50 },
                            ]);
                          }}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Variant
                        </Button>
                      </div>
                      {abTestVariants.map((variant, index) => (
                        <Card key={variant.id} className="p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium">Variant {index + 1}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() =>
                                setAbTestVariants(abTestVariants.filter((v) => v.id !== variant.id))
                              }
                            >
                              &times;
                            </Button>
                          </div>
                          <Input
                            value={variant.title}
                            onChange={(e) => {
                              const updated = [...abTestVariants];
                              updated[index].title = e.target.value;
                              setAbTestVariants(updated);
                            }}
                            placeholder="Title"
                            className="h-8 text-sm"
                          />
                          <Textarea
                            value={variant.body}
                            onChange={(e) => {
                              const updated = [...abTestVariants];
                              updated[index].body = e.target.value;
                              setAbTestVariants(updated);
                            }}
                            placeholder="Body"
                            rows={2}
                            className="text-sm"
                          />
                          <div className="flex items-center gap-2">
                            <Label className="text-xs whitespace-nowrap">Weight %</Label>
                            <Input
                              type="number"
                              min="1"
                              max="100"
                              value={variant.weight}
                              onChange={(e) => {
                                const updated = [...abTestVariants];
                                updated[index].weight = parseInt(e.target.value) || 0;
                                setAbTestVariants(updated);
                              }}
                              className="h-8 text-sm w-20"
                            />
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>

                {/* Image Override */}
                <div>
                  <Label className="text-sm font-medium">Image Override</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Override the template's default image
                  </p>
                  <MediaPicker
                    onSelect={setImageUrl}
                    currentValue={imageUrl}
                    allowedTypes={[
                      "image/jpeg",
                      "image/jpg",
                      "image/png",
                      "image/webp",
                    ]}
                    title="Select Notification Image"
                    description="Choose an image from your media library, upload a new one, or use an external URL"
                    buttonText="Select Image"
                    showPreview={true}
                  />
                  {selectedTemplate?.image_url && !imageUrl && (
                    <div className="mt-1 p-2 bg-muted rounded-md">
                      <p className="text-xs text-muted-foreground">
                        <strong>Template default:</strong> {selectedTemplate.image_url}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </AdminLayout>
  );
}
